import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {Menu, X, Globe, Calendar, Shield} from 'lucide-react';
import { lumi } from '../lib/lumi';
import { motion, AnimatePresence } from 'framer-motion';
import BookingModal from './BookingModal';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const location = useLocation();
  const user = lumi.auth.user;

  // Scroll to top on route change
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Trip Types', path: '/trip-types' },
    { name: 'Travel Essentials', path: '/travel-essentials' },
    { name: 'Resources', path: '/resources' },
    { name: 'FAQ', path: '/faq' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  const isAdmin = user?.userRole === 'ADMIN';

  return (
    <div className="min-h-screen bg-white dark:bg-navy-900 text-gray-900 dark:text-white font-sans selection:bg-cyan-400 selection:text-navy-900">
      {/* Sticky Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 dark:bg-navy-900/80 backdrop-blur-md border-b border-gray-200 dark:border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center group">
              <img 
                src="https://static.lumi.new/7e/7e8c11592d59c577a4659cc3ca11dbdf.webp" 
                alt="Haven Travel Health Logo" 
                className="h-12 w-12 sm:h-14 sm:w-14 object-cover transition-transform group-hover:scale-105 bg-white rounded-full p-1"
              />
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-6">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-medium transition-colors hover:text-cyan-500 ${
                    location.pathname === link.path
                      ? 'text-cyan-600 dark:text-cyan-400'
                      : 'text-gray-600 dark:text-gray-300'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* Desktop CTAs */}
            <div className="hidden lg:flex items-center gap-4">
              <button
                onClick={() => setIsBookingModalOpen(true)}
                className="px-5 py-2.5 bg-blue-500 hover:bg-blue-600 text-white text-sm font-bold rounded-full shadow-lg shadow-blue-500/25 transition-all hover:scale-105 active:scale-95"
              >
                Book Virtual Consult
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5 rounded-lg transition-colors"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden border-t border-gray-200 dark:border-white/10 bg-white dark:bg-navy-900 overflow-hidden"
            >
              <div className="px-4 py-4 space-y-3">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsMenuOpen(false)}
                    className="block text-base font-medium text-gray-900 dark:text-white hover:text-cyan-500"
                  >
                    {link.name}
                  </Link>
                ))}
                <div className="pt-4 border-t border-gray-200 dark:border-white/10 flex flex-col gap-4">
                  <button
                    onClick={() => {
                      setIsMenuOpen(false);
                      setIsBookingModalOpen(true);
                    }}
                    className="w-full py-3 bg-blue-500 text-white text-center font-bold rounded-xl shadow-lg"
                  >
                    Book Virtual Consult
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Main Content */}
      <main className="pt-16 sm:pt-20 min-h-[calc(100vh-64px)] sm:min-h-[calc(100vh-80px)]">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-navy-900 border-t border-white/10 pt-12 sm:pt-16 pb-6 sm:pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10 lg:gap-12 mb-8 sm:mb-12">
            {/* Brand */}
            <div className="space-y-3 sm:space-y-4">
              <img 
                src="https://static.lumi.new/7e/7e8c11592d59c577a4659cc3ca11dbdf.webp" 
                alt="Haven Travel Health Logo" 
                className="h-12 w-12 object-cover bg-white rounded-full p-1"
              />
              <p className="text-gray-400 text-sm leading-relaxed">
                British Columbia's premier virtual travel health clinic. Expert advice, coordinated vaccinations, and peace of mind for your journey.
              </p>
            </div>

            {/* Services */}
            <div>
              <h3 className="font-heading font-bold text-white mb-4 sm:mb-6 text-base sm:text-lg">Services</h3>
              <ul className="space-y-3 text-sm text-gray-400">
                <li><Link to="/trip-types" className="hover:text-cyan-400 transition-colors">Group & Family Travel</Link></li>
                <li><Link to="/travel-essentials" className="hover:text-cyan-400 transition-colors">Travel Essentials</Link></li>
                <li><Link to="/resources" className="hover:text-cyan-400 transition-colors">Travel Health Resources</Link></li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h3 className="font-heading font-bold text-white mb-4 sm:mb-6 text-base sm:text-lg">Company</h3>
              <ul className="space-y-3 text-sm text-gray-400">
                <li><Link to="/about" className="hover:text-cyan-400 transition-colors">About Us</Link></li>
                <li><Link to="/faq" className="hover:text-cyan-400 transition-colors">FAQ</Link></li>
                <li><Link to="/contact" className="hover:text-cyan-400 transition-colors">Contact</Link></li>
                <li><Link to="/admin" className="hover:text-cyan-400 transition-colors flex items-center gap-1">
                  <Shield className="w-3 h-3" />
                  Admin Portal
                </Link></li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h3 className="font-heading font-bold text-white mb-4 sm:mb-6 text-base sm:text-lg">Contact</h3>
              <ul className="space-y-3 text-sm text-gray-400">
                <li className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-cyan-400 shrink-0" />
                  <span>Appointments available evenings & weekends</span>
                </li>
                <li className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-cyan-400 shrink-0" />
                  <a href="mailto:saba@haventravelhealth.ca" className="hover:text-cyan-400 transition-colors">
                    saba@haventravelhealth.ca
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/10 pt-6 sm:pt-8 space-y-3 sm:space-y-4">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-xs text-gray-500 text-center md:text-left">
                © {new Date().getFullYear()} Haven Travel Health. All rights reserved.
              </p>
              <p className="text-xs text-gray-600 text-center md:text-right max-w-md">
                Disclaimer: Information provided on this website is for educational purposes only and does not replace individualized medical advice.
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500">
                Haven Travel Health Powered by Voyage Vax
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Booking Modal */}
      <BookingModal 
        isOpen={isBookingModalOpen} 
        onClose={() => setIsBookingModalOpen(false)} 
      />
    </div>
  );
};

export default Layout;